import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, Edit, UsersRound, X } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { teamApi, TeamRecord, TeamUpsert, venueBotMappingApi, VenueBotMapping } from '../../services/api';
import Colors from '../../constants/Colors';
import { isKnownVenue } from '../../utils/venueOptions';

function formatImageFieldLabel(label: string) {
  switch (label) {
    case 'MainLogo':
      return 'Main Logo';
    case 'FootballRightLogo':
      return 'Football Right Logo';
    case 'FootballLeftLogo':
      return 'Football Left Logo';
    case 'BasketballLogo':
      return 'Basketball Logo';
    default:
      return label;
  }
}

const TEAM_THEME_COLOR_PRESETS = [
  '#0E2569',
  '#782F40',
  '#BA0C2F',
  '#B3A369',
  '#7BAFD4',
  '#000000',
];

const TEAM_TEXT_COLOR_PRESETS = ['#0A0A16', '#6B5118', '#000000', '#FFFFFF'];
const SPORT_LABELS: Record<'football' | 'basketball', { title: string; subtitle: string }> = {
  football: {
    title: 'Football',
    subtitle: 'Team appears in football schedules and lineups.',
  },
  basketball: {
    title: 'Basketball',
    subtitle: 'Team appears in basketball schedules and lineups.',
  },
};

const EMPTY_FORM: TeamUpsert = {
  name: '',
  abbreviation: '',
  game_types: ['football'],
  status: 'active',
  venue: '',
  basketball_venue: '',
  theme_color: '#0E2569',
  text_color: '#FFFFFF',
  main_logo_url: '',
  football_logo_right_url: '',
  football_logo_left_url: '',
  basketball_logo_url: '',
  audio_url: '',
  school_name: '',
  catch_phrase: '',
  commentator: '',
  basketball_commentator: '',
};

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function normalizeHexColor(value: string) {
  const trimmed = value.trim();
  const prefixed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (/^#[0-9a-fA-F]{6}$/.test(prefixed)) {
    return prefixed.toUpperCase();
  }
  return null;
}

type ColorPickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  presets?: string[];
  allowCustomHex?: boolean;
};

function ColorPicker({ label, value, onChange, presets, allowCustomHex = true }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customHexValue, setCustomHexValue] = useState(value.toUpperCase());
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setCustomHexValue(value.toUpperCase());
  }, [value]);

  const applyCustomHex = () => {
    const normalized = normalizeHexColor(customHexValue);
    if (normalized) {
      onChange(normalized);
      setCustomHexValue(normalized);
    }
  };

  return (
    <div className="teams-color-picker" ref={containerRef}>
      <span className="form-label">{label}</span>
      <button
        type="button"
        className="teams-color-trigger"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="teams-color-chip" style={{ backgroundColor: value }} />
        <span>{value.toUpperCase()}</span>
      </button>

      {isOpen && (
        <div className="teams-color-popover">
          <div className="teams-color-popover-header">
            <div>
              <div className="teams-color-popover-title">{label}</div>
              <div className="teams-color-popover-subtitle">Choose from presets or tune a custom value.</div>
            </div>
            <button
              type="button"
              className="teams-color-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close color picker"
            >
              <X size={14} />
            </button>
          </div>

          <div className="teams-color-grid">
            {(presets || TEAM_THEME_COLOR_PRESETS).map((preset) => (
              <button
                key={preset}
                type="button"
                className={`teams-color-option ${value.toLowerCase() === preset.toLowerCase() ? 'active' : ''}`}
                onClick={() => onChange(preset)}
              >
                <span className="teams-color-swatch" style={{ backgroundColor: preset }} />
                <span>{preset}</span>
              </button>
            ))}
          </div>

          {allowCustomHex && (
            <div className="teams-color-custom">
              <label htmlFor={`team-color-input-${label.toLowerCase().replace(/\s+/g, '-')}`}>Custom hex</label>
              <div className="teams-color-custom-input-row">
                <input
                  id={`team-color-input-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="teams-color-hex-input"
                  type="text"
                  value={customHexValue}
                  onChange={(event) => setCustomHexValue(event.target.value)}
                  onBlur={applyCustomHex}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      applyCustomHex();
                    }
                  }}
                  placeholder="#0E2569"
                />
                <input
                  className="teams-color-picker-input"
                  type="color"
                  value={value}
                  onChange={(event) => onChange(event.target.value.toUpperCase())}
                />
                {/* <button
                  type="button"
                  className="teams-color-apply"
                  onClick={applyCustomHex}
                >
                  Apply
                </button> */}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type LogoUploadProps = {
  label: string;
  helpText: string;
  logoFile: File | null;
  onLogoFileChange: (file: File | null) => void;
  onClear: () => void;
  previewUrl: string | null;
  uploading: boolean;
};

function LogoUpload({
  label,
  helpText,
  logoFile,
  onLogoFileChange,
  onClear,
  previewUrl,
  uploading,
}: LogoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const displayPreview = previewUrl;
  const displayLabel = formatImageFieldLabel(label);
  const previewClass =
    label === 'MainLogo'
      ? 'teams-logo-upload-preview teams-logo-upload-preview-main'
      : label === 'FootballRightLogo' || label === 'FootballLeftLogo'
        ? 'teams-logo-upload-preview teams-logo-upload-preview-thumbnail'
        : 'teams-logo-upload-preview teams-logo-upload-preview-banner';

  return (
    <div className="teams-logo-upload">
      <div className="teams-logo-upload-header">
        <span className="form-label">{displayLabel}</span>
        <span className={`teams-upload-badge ${logoFile || previewUrl ? 'ready' : 'empty'}`}>
          {logoFile || previewUrl ? 'Ready' : 'Empty'}
        </span>
      </div>
      <div className="teams-logo-upload-shell">
        {displayPreview ? (
          <img
            src={displayPreview}
            alt={`${label} preview`}
            className={previewClass}
            onError={(event) => {
              (event.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="teams-logo-upload-empty">
            <UsersRound size={22} />
            <span>No logo selected</span>
          </div>
        )}

        <div className="teams-logo-upload-copy">
          <div className="teams-logo-upload-title" title={logoFile ? logoFile.name : `Drop or choose ${displayLabel.toLowerCase()}`}>
            {logoFile ? logoFile.name : `Drop or choose ${displayLabel.toLowerCase()}`}
          </div>
          <div className="teams-logo-upload-subtitle">{helpText}</div>
          <div className="button-group teams-upload-actions">
            <Button
              type="button"
              className='btn-small'
              variant="secondary"
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {logoFile ? 'Change File' : 'Choose File'}
            </Button>
            <Button
              type="button"
              className='btn-small'
              variant="outline"
              onClick={() => {
                onClear();
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            >
              Clear
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(event) => onLogoFileChange(event.target.files?.[0] || null)}
          />
        </div>
      </div>
    </div>
  );
}

export default function TeamsPanel() {
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [form, setForm] = useState<TeamUpsert>(EMPTY_FORM);
  const [activeEditorTab, setActiveEditorTab] = useState<'details' | 'logo'>('details');
  const [mainLogoFile, setMainLogoFile] = useState<File | null>(null);
  const [footballRightLogoFile, setFootballRightLogoFile] = useState<File | null>(null);
  const [footballLeftLogoFile, setFootballLeftLogoFile] = useState<File | null>(null);
  const [basketballLogoFile, setBasketballLogoFile] = useState<File | null>(null);
  const [mainLogoPreviewUrl, setMainLogoPreviewUrl] = useState<string | null>(null);
  const [footballRightLogoPreviewUrl, setFootballRightLogoPreviewUrl] = useState<string | null>(null);
  const [footballLeftLogoPreviewUrl, setFootballLeftLogoPreviewUrl] = useState<string | null>(null);
  const [basketballLogoPreviewUrl, setBasketballLogoPreviewUrl] = useState<string | null>(null);
  const [mainLogoRemoved, setMainLogoRemoved] = useState(false);
  const [footballRightLogoRemoved, setFootballRightLogoRemoved] = useState(false);
  const [footballLeftLogoRemoved, setFootballLeftLogoRemoved] = useState(false);
  const [basketballLogoRemoved, setBasketballLogoRemoved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [venueOptions, setVenueOptions] = useState<VenueBotMapping[]>([]);

  const handleMainLogoFileChange = (file: File | null) => {
    setMainLogoRemoved(false);
    setMainLogoFile(file);
  };

  const handleFootballRightLogoFileChange = (file: File | null) => {
    setFootballRightLogoRemoved(false);
    setFootballRightLogoFile(file);
  };

  const handleFootballLeftLogoFileChange = (file: File | null) => {
    setFootballLeftLogoRemoved(false);
    setFootballLeftLogoFile(file);
  };

  const handleBasketballLogoFileChange = (file: File | null) => {
    setBasketballLogoRemoved(false);
    setBasketballLogoFile(file);
  };

  const clearMainLogo = () => {
    setMainLogoFile(null);
    setMainLogoRemoved(true);
  };

  const clearFootballRightLogo = () => {
    setFootballRightLogoFile(null);
    setFootballRightLogoRemoved(true);
  };

  const clearFootballLeftLogo = () => {
    setFootballLeftLogoFile(null);
    setFootballLeftLogoRemoved(true);
  };

  const clearBasketballLogo = () => {
    setBasketballLogoFile(null);
    setBasketballLogoRemoved(true);
  };

  const loadTeams = async () => {
    setLoading(true);
    setError(null);

    const response = await teamApi.listTeams();
    if (response.success && response.data) {
      setTeams(response.data.teams || []);
    } else {
      setError(response.error || 'Failed to load team data');
    }

    setLoading(false);
  };

  useEffect(() => {
    loadTeams();
  }, []);

  useEffect(() => {
    const loadVenueOptions = async () => {
      const response = await venueBotMappingApi.listVenueBotMappings();
      if (response.success && response.data) {
        setVenueOptions(response.data.venues || []);
      }
    };

    loadVenueOptions();
  }, []);

  useEffect(() => {
    if (!mainLogoFile) {
      setMainLogoPreviewUrl(null);
      return;
    }

    const nextPreview = URL.createObjectURL(mainLogoFile);
    setMainLogoPreviewUrl(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [mainLogoFile]);

  useEffect(() => {
    if (!footballRightLogoFile) {
      setFootballRightLogoPreviewUrl(null);
      return;
    }

    const nextPreview = URL.createObjectURL(footballRightLogoFile);
    setFootballRightLogoPreviewUrl(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [footballRightLogoFile]);

  useEffect(() => {
    if (!footballLeftLogoFile) {
      setFootballLeftLogoPreviewUrl(null);
      return;
    }

    const nextPreview = URL.createObjectURL(footballLeftLogoFile);
    setFootballLeftLogoPreviewUrl(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [footballLeftLogoFile]);

  useEffect(() => {
    if (!basketballLogoFile) {
      setBasketballLogoPreviewUrl(null);
      return;
    }

    const nextPreview = URL.createObjectURL(basketballLogoFile);
    setBasketballLogoPreviewUrl(nextPreview);
    return () => URL.revokeObjectURL(nextPreview);
  }, [basketballLogoFile]);

  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  const filteredTeams = useMemo(() => {
    const query = search.trim().toLowerCase();
    return teams
      .filter((team) =>
        !query ||
        [
          team.name,
          team.abbreviation,
          team.venue,
          team.basketball_venue,
          team.school_name,
          team.catch_phrase,
          team.commentator,
          team.basketball_commentator,
        ]
          .join(' ')
          .toLowerCase()
          .includes(query)
      )
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === 'active' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
  }, [search, teams]);

  const openCreateModal = () => {
    setEditingTeamId(null);
    setForm(EMPTY_FORM);
    setActiveEditorTab('details');
    setMainLogoFile(null);
    setFootballRightLogoFile(null);
    setFootballLeftLogoFile(null);
    setBasketballLogoFile(null);
    setMainLogoPreviewUrl(null);
    setFootballRightLogoPreviewUrl(null);
    setFootballLeftLogoPreviewUrl(null);
    setBasketballLogoPreviewUrl(null);
    setMainLogoRemoved(false);
    setFootballRightLogoRemoved(false);
    setFootballLeftLogoRemoved(false);
    setBasketballLogoRemoved(false);
    setLogoUploading(false);
    setIsModalOpen(true);
  };

  const openEditModal = (team: TeamRecord) => {
    const isInactive = team.status === 'inactive';
    setEditingTeamId(team.team_id);
    setActiveEditorTab('details');
    setForm({
      name: team.name,
      abbreviation: team.abbreviation,
      game_types: team.game_types || [team.game_type],
      status: team.status,
      venue: team.venue,
      basketball_venue: team.basketball_venue || '',
      theme_color: team.theme_color,
      text_color: team.text_color || '#FFFFFF',
      main_logo_url: team.main_logo_url,
      football_logo_right_url: team.football_logo_right_url || '',
      football_logo_left_url: team.football_logo_left_url || '',
      basketball_logo_url: team.basketball_logo_url || '',
      audio_url: isInactive ? '' : team.audio_url || '',
      school_name: team.school_name,
      catch_phrase: team.catch_phrase || '',
      commentator: isInactive ? '' : team.commentator,
      basketball_commentator: isInactive ? '' : team.basketball_commentator || '',
    });
    setMainLogoFile(null);
    setFootballRightLogoFile(null);
    setFootballLeftLogoFile(null);
    setBasketballLogoFile(null);
    setMainLogoPreviewUrl(null);
    setFootballRightLogoPreviewUrl(null);
    setFootballLeftLogoPreviewUrl(null);
    setBasketballLogoPreviewUrl(null);
    setMainLogoRemoved(false);
    setFootballRightLogoRemoved(false);
    setFootballLeftLogoRemoved(false);
    setBasketballLogoRemoved(false);
    setLogoUploading(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveEditorTab('details');
    setMainLogoFile(null);
    setFootballRightLogoFile(null);
    setFootballLeftLogoFile(null);
    setBasketballLogoFile(null);
    setMainLogoPreviewUrl(null);
    setFootballRightLogoPreviewUrl(null);
    setFootballLeftLogoPreviewUrl(null);
    setBasketballLogoPreviewUrl(null);
    setMainLogoRemoved(false);
    setFootballRightLogoRemoved(false);
    setFootballLeftLogoRemoved(false);
    setBasketballLogoRemoved(false);
    setLogoUploading(false);
  };

  const resetDraft = () => {
    if (editingTeamId) {
      const current = teams.find((team) => team.team_id === editingTeamId);
      if (current) {
        const isInactive = current.status === 'inactive';
        setForm({
          name: current.name,
          abbreviation: current.abbreviation,
          game_types: current.game_types || [current.game_type],
          status: current.status,
          venue: current.venue,
          basketball_venue: current.basketball_venue || '',
          theme_color: current.theme_color,
          text_color: current.text_color || '#FFFFFF',
          main_logo_url: current.main_logo_url,
          football_logo_right_url: current.football_logo_right_url || '',
          football_logo_left_url: current.football_logo_left_url || '',
          basketball_logo_url: current.basketball_logo_url || '',
          audio_url: isInactive ? '' : current.audio_url || '',
          school_name: current.school_name,
          catch_phrase: current.catch_phrase || '',
          commentator: isInactive ? '' : current.commentator,
          basketball_commentator: isInactive ? '' : current.basketball_commentator || '',
        });
      }
    } else {
      setForm(EMPTY_FORM);
    }
    setActiveEditorTab('details');

    setMainLogoFile(null);
    setFootballRightLogoFile(null);
    setFootballLeftLogoFile(null);
    setBasketballLogoFile(null);
    setMainLogoPreviewUrl(null);
    setFootballRightLogoPreviewUrl(null);
    setFootballLeftLogoPreviewUrl(null);
    setBasketballLogoPreviewUrl(null);
    setMainLogoRemoved(false);
    setFootballRightLogoRemoved(false);
    setFootballLeftLogoRemoved(false);
    setBasketballLogoRemoved(false);
    setLogoUploading(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.name.trim() || !form.abbreviation.trim()) {
      toast.error('Team name and abbreviation are required.');
      return;
    }
    if (!form.game_types || form.game_types.length === 0) {
      toast.error('Select at least one sport.');
      return;
    }
    if (form.status === 'active') {
      const activeGameTypes = form.game_types || [];
      if (activeGameTypes.includes('football')) {
        if (!form.venue.trim()) {
          toast.error('Football venue is required for active teams.');
          return;
        }
        if (!form.commentator.trim()) {
          toast.error('Football commentator is required for active teams.');
          return;
        }
      }
      if (activeGameTypes.includes('basketball')) {
        if (!form.basketball_venue.trim()) {
          toast.error('Basketball venue is required for active teams.');
          return;
        }
        if (!form.basketball_commentator.trim()) {
          toast.error('Basketball commentator is required for active teams.');
          return;
        }
      }
      if (!form.audio_url.trim()) {
        toast.error('Audio URL is required for active teams.');
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const payload: TeamUpsert = {
        ...form,
        abbreviation: form.abbreviation.trim().toUpperCase(),
        status: form.status,
        theme_color: form.theme_color.trim(),
        text_color: (form.text_color || '#FFFFFF').trim(),
        audio_url: form.status === 'inactive' ? '' : form.audio_url.trim(),
        commentator: form.status === 'inactive' ? '' : form.commentator.trim(),
        basketball_venue: form.basketball_venue.trim(),
        basketball_commentator: form.status === 'inactive' ? '' : form.basketball_commentator.trim(),
        football_logo_right_url: form.football_logo_right_url.trim(),
        football_logo_left_url: form.football_logo_left_url.trim(),
      };

      if (mainLogoRemoved && !mainLogoFile) {
        payload.main_logo_url = '';
      }
      if (footballRightLogoRemoved && !footballRightLogoFile) {
        payload.football_logo_right_url = '';
      }
      if (footballLeftLogoRemoved && !footballLeftLogoFile) {
        payload.football_logo_left_url = '';
      }
      if (basketballLogoRemoved && !basketballLogoFile) {
        payload.basketball_logo_url = '';
      }

      if (mainLogoFile) {
        setLogoUploading(true);
        const uploadResponse = await teamApi.uploadTeamImage(mainLogoFile, 'main');
        setLogoUploading(false);

        if (!uploadResponse.success || !uploadResponse.data?.url) {
          const uploadError = uploadResponse.error || 'Unable to upload logo image';
          setError(uploadError);
          toast.error(uploadError);
          setSaving(false);
          return;
        }

        payload.main_logo_url = uploadResponse.data.url;
      }

      if (footballRightLogoFile) {
        setLogoUploading(true);
        const uploadResponse = await teamApi.uploadTeamImage(footballRightLogoFile, 'thumbnail');
        setLogoUploading(false);

        if (!uploadResponse.success || !uploadResponse.data?.url) {
          const uploadError = uploadResponse.error || 'Unable to upload football right logo';
          setError(uploadError);
          toast.error(uploadError);
          setSaving(false);
          return;
        }

        payload.football_logo_right_url = uploadResponse.data.url;
      }

      if (footballLeftLogoFile) {
        setLogoUploading(true);
        const uploadResponse = await teamApi.uploadTeamImage(footballLeftLogoFile, 'banner');
        setLogoUploading(false);

        if (!uploadResponse.success || !uploadResponse.data?.url) {
          const uploadError = uploadResponse.error || 'Unable to upload football left logo';
          setError(uploadError);
          toast.error(uploadError);
          setSaving(false);
          return;
        }

        payload.football_logo_left_url = uploadResponse.data.url;
      }

      if (basketballLogoFile) {
        setLogoUploading(true);
        const uploadResponse = await teamApi.uploadTeamImage(basketballLogoFile, 'banner');
        setLogoUploading(false);

        if (!uploadResponse.success || !uploadResponse.data?.url) {
          const uploadError = uploadResponse.error || 'Unable to upload basketball logo';
          setError(uploadError);
          toast.error(uploadError);
          setSaving(false);
          return;
        }

        payload.basketball_logo_url = uploadResponse.data.url;
      }

      if (payload.status === 'inactive') {
        payload.audio_url = '';
        payload.commentator = '';
        payload.basketball_commentator = '';
      }

      const response = editingTeamId
        ? await teamApi.updateTeam(editingTeamId, payload)
        : await teamApi.createTeam(payload);

      if (response.success && response.data?.team) {
        const nextTeam = response.data.team;
        setTeams((current) =>
          editingTeamId
            ? current.map((team) => (team.team_id === nextTeam.team_id ? nextTeam : team))
            : [nextTeam, ...current]
        );
        toast.success(editingTeamId ? 'Team updated.' : 'Team added.');
        closeModal();
      } else {
        const saveError = response.error || 'Unable to save school';
        setError(saveError);
        toast.error(saveError);
      }
    } finally {
      setLogoUploading(false);
      setSaving(false);
    }
  };

  const handleDelete = async (team: TeamRecord) => {
    const confirmed = window.confirm(`Delete ${team.name}?`);
    if (!confirmed) return;

    const response = await teamApi.deleteTeam(team.team_id);
    if (response.success) {
      setTeams((current) => current.filter((item) => item.team_id !== team.team_id));
      if (editingTeamId === team.team_id) {
        closeModal();
      }
      toast.success('Team deleted.');
    } else {
      toast.error(response.error || 'Unable to delete team');
    }
  };

  const currentThemeColor = form.theme_color || '#0E2569';
  const currentTextColor = form.text_color || '#FFFFFF';
  const previewName = form.name || 'Team preview';
  const previewAbbreviation = form.abbreviation || 'ABC';
  const mainPreview = mainLogoRemoved ? null : mainLogoPreviewUrl || form.main_logo_url;
  const footballRightPreview = footballRightLogoRemoved ? null : footballRightLogoPreviewUrl || form.football_logo_right_url;
  const footballLeftPreview = footballLeftLogoRemoved ? null : footballLeftLogoPreviewUrl || form.football_logo_left_url;
  const basketballPreview = basketballLogoRemoved ? null : basketballLogoPreviewUrl || form.basketball_logo_url;
  const statusBadgeClass = form.status === 'active' ? 'Active' : 'Inactive';
  const selectedGameTypes = form.game_types || [];

  return (
    <div className="section seasons-section teams-section">
      <div className="seasons-topbar teams-topbar">
        <div>
          <h2 className="section-title seasons-section-title">
            <UsersRound size={24} />
            School Management
          </h2>
          <p className="seasons-subtitle-copy teams-subtitle-copy">
            Manage football and basketball venues, commentators, logos, and team details per sport.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-small btn-primary seasons-refresh-button teams-refresh-button"
          onClick={openCreateModal}
        >
          Create New School
        </button>
      </div>

      <div className="seasons-columns">
        <Card
          title="School List"
          description="Open any school record to review or edit its metadata."
          headerAction={
            <div style={{ minWidth: 'min(100%, 320px)' }}>
              <Input
                label=""
                value={search}
                onChange={setSearch}
                placeholder="Search by name, venue, school, catch phrase, or commentator"
              />
            </div>
          }
        >
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div className="teams-table-wrap">
            <table className="seasons-table teams-table">
              <thead>
                <tr>
                  <th>Team</th>
                  <th>Sports</th>
                  <th>Status</th>
                  <th>FB Venue</th>
                  <th>BB Venue</th>
                  <th>School</th>
                  <th>Theme</th>
                  <th>FB Commentator</th>
                  <th>BB Commentator</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.map((team) => (
                  <tr key={team.team_id}>
                    <td>
                      <div className="teams-table-team">
                        <div
                          className="teams-table-preview-card teams-table-preview-card--glass"
                          style={{
                            backgroundColor: team.theme_color || '#0E2569',
                            color: team.text_color || '#FFFFFF',
                            // Tints the glass glow underneath with the team colour.
                            ['--team-glow' as string]: `${team.theme_color || '#0E2569'}55`,
                          } as React.CSSProperties}
                        >
                          {team.main_logo_url ? (
                            <img src={team.main_logo_url} alt={team.name} className="teams-table-logo" />
                          ) : (
                            <div
                              className="teams-table-logo teams-logo-fallback"
                              style={{
                                backgroundColor: `${team.text_color || '#FFFFFF'}18`,
                                color: team.text_color || '#FFFFFF',
                              }}
                            >
                              {getInitials(team.name)}
                            </div>
                          )}
                          <div className="teams-table-preview-copy" style={{ color: team.text_color || '#FFFFFF' }}>
                            <div className="teams-table-preview-name">{team.name}</div>
                            <div className="teams-table-subtitle">{team.abbreviation}</div>
                            <div className="teams-table-preview-meta">
                              {(team.game_types || [team.game_type]).includes('football') && team.venue
                                ? `FB: ${team.venue}`
                                : (team.game_types || [team.game_type]).includes('basketball') && team.basketball_venue
                                  ? `BB: ${team.basketball_venue}`
                                  : 'Venue'}{' '}
                              · {team.school_name || 'School'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {(team.game_types || [team.game_type]).map((gameType) => (
                          <span key={gameType} className={`teams-type-badge ${gameType}`}>
                            {gameType}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`seasons-status-badge ${team.status === 'active' ? 'active' : 'inactive'}`}>
                        {team.status}
                      </span>
                    </td>
                    <td>{team.venue || '-'}</td>
                    <td>{team.basketball_venue || '-'}</td>
                    <td>{team.school_name || '-'}</td>
                    <td>
                      <div className="teams-color-cell">
                        <span className="teams-color-swatch" style={{ backgroundColor: team.theme_color || '#0E2569' }} />
                        <span>{team.theme_color || '-'}</span>
                      </div>
                    </td>
                    <td>{team.commentator || '-'}</td>
                    <td>{team.basketball_commentator || '-'}</td>
                    <td>
                      <div className="seasons-list-actions teams-list-actions">
                        <button
                          type="button"
                          className="seasons-view-button"
                          onClick={() => openEditModal(team)}
                        >
                          Edit
                          <Edit size={14} />
                        </button>
                        <button
                          type="button"
                          className="list-delete-button"
                          onClick={() => handleDelete(team)}
                          aria-label={`Delete team ${team.name}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && filteredTeams.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <div className="seasons-footer-message">
                        No teams found. Create one to get started.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isModalOpen && (
        <div className="seasons-modal-overlay" role="presentation" onClick={closeModal}>
          <div
            className="seasons-modal-panel teams-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Team record editor"
            onClick={(event) => event.stopPropagation()}
          >
            <Card
              title={editingTeamId ? 'Edit School' : 'Create School'}
              description={
                editingTeamId
                  ? 'Review or update the selected team record.'
                  : 'Add a new team record and optionally upload a logo to S3.'
              }
              headerAction={
                <button
                  type="button"
                  className="seasons-modal-close"
                  onClick={closeModal}
                  aria-label="Close team editor"
                >
                  <X size={16} />
                </button>
              }
            >
              <div className="teams-modal-shell">
                <div
                  className="teams-preview-card teams-preview-card--glass"
                  style={{
                    backgroundColor: currentThemeColor,
                    color: currentTextColor,
                    // Tints the glass glow underneath with the team colour.
                    ['--team-glow' as string]: `${currentThemeColor}55`,
                  } as React.CSSProperties}
                >
                  <div
                    className="teams-preview-logo"
                    style={{ backgroundColor: `${currentTextColor}14`, borderColor: `${currentTextColor}33` }}
                  >
                    {mainPreview ? (
                      <img
                        src={mainPreview}
                        alt={`${previewName} logo preview`}
                        className="teams-preview-logo-image"
                      />
                    ) : (
                      <span>{getInitials(previewName) || <UsersRound size={18} />}</span>
                    )}
                  </div>
                  <div className="teams-preview-copy" style={{ color: currentTextColor }}>
                    <div className="teams-preview-title">{previewName}</div>
                    <div className="teams-preview-chip">{previewAbbreviation}</div>
                    <div className={`teams-preview-status ${statusBadgeClass}`}>
                      {form.status === 'active' ? 'Active team' : 'Inactive team'}
                    </div>
                    <div className="teams-meta" style={{ marginTop: '8px' }}>
                      {form.venue || 'Venue'} · {form.school_name || 'School name'}
                    </div>
                    {form.catch_phrase ? (
                      <div className="teams-meta" style={{ marginTop: '4px', fontStyle: 'italic' }}>
                        “{form.catch_phrase}”
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={`seasons-sport-toggle teams-preview-toggle ${form.status === 'active' ? 'on' : 'off'}`}
                    onClick={() =>
                      setForm((prev) => {
                        const nextStatus = prev.status === 'active' ? 'inactive' : 'active';
                        return {
                          ...prev,
                          status: nextStatus,
                          commentator: nextStatus === 'inactive' ? '' : prev.commentator,
                          basketball_commentator: nextStatus === 'inactive' ? '' : prev.basketball_commentator,
                          audio_url: nextStatus === 'inactive' ? '' : prev.audio_url,
                        };
                      })
                    }
                    aria-label="Toggle team status"
                    style={{ color: currentTextColor }}
                  >
                    <span className="seasons-sport-toggle-track">
                      <span className="seasons-sport-toggle-thumb" />
                    </span>
                    <span className="seasons-sport-toggle-text">
                      {form.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </div>

                <div className="teams-editor-tabs">
                  <button
                    type="button"
                    className={`teams-editor-tab ${activeEditorTab === 'details' ? 'active' : ''}`}
                    onClick={() => setActiveEditorTab('details')}
                  >
                    School Details
                  </button>
                  <button
                    type="button"
                    className={`teams-editor-tab ${activeEditorTab === 'logo' ? 'active' : ''}`}
                    onClick={() => setActiveEditorTab('logo')}
                  >
                    School Logo
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {activeEditorTab === 'details' && (
                    <div className="teams-form-grid">
                      <div className="teams-section-header teams-full-width">
                        <div className="teams-section-title">Details</div>
                        <div className="teams-section-subtitle">
                          Basic team information and branding colors.
                        </div>
                      </div>

                      <Input
                        label="Team name"
                        value={form.name}
                        onChange={(value) => setForm({ ...form, name: value })}
                        placeholder="North Ridge Eagles"
                        required
                      />
                      <Input
                        label="Abbreviation"
                        value={form.abbreviation}
                        onChange={(value) => setForm({ ...form, abbreviation: value.toUpperCase() })}
                        placeholder="NRE"
                        required
                      />
                      <ColorPicker
                        label="Theme Color"
                        value={currentThemeColor}
                        onChange={(value) => setForm({ ...form, theme_color: value })}
                        presets={TEAM_THEME_COLOR_PRESETS}
                        allowCustomHex
                      />
                      <ColorPicker
                        label="Text Color"
                        value={currentTextColor}
                        onChange={(value) => setForm({ ...form, text_color: value })}
                        presets={TEAM_TEXT_COLOR_PRESETS}
                      />
                      <Input
                        label="School name"
                        value={form.school_name}
                        onChange={(value) => setForm({ ...form, school_name: value })}
                        placeholder="North Ridge High School"
                      />
                      <Input
                        label="Catch phrase"
                        value={form.catch_phrase}
                        onChange={(value) => setForm({ ...form, catch_phrase: value })}
                        placeholder="Go Eagles!"
                      />
                      <Input
                        label="Audio URL"
                        value={form.audio_url}
                        onChange={(value) => setForm({ ...form, audio_url: value })}
                        placeholder="https://stream.example.com/audio.m3u8"
                        type="url"
                        disabled={form.status === 'inactive'}
                        required={form.status === 'active'}
                      />
                      {form.status === 'inactive' && (
                        <div className="teams-inactive-note teams-full-width">
                          Inactive teams do not use commentator or audio URL values, so those fields are cleared and locked while the team is inactive.
                        </div>
                      )}

                      <div className="teams-section-header teams-full-width">
                        <div className="teams-section-title">Sports Team</div>
                        <div className="teams-section-subtitle">
                          Select every sport this team plays.
                        </div>
                      </div>

                      <div className="teams-sport-grid teams-full-width">
                        {(['football', 'basketball'] as const).map((gameType) => {
                          const active = selectedGameTypes.includes(gameType);
                          const isFootball = gameType === 'football';
                          const venueField = isFootball ? 'venue' : 'basketball_venue';
                          const commentatorField = isFootball ? 'commentator' : 'basketball_commentator';
                          const venueValue = form[venueField];
                          const commentatorValue = form[commentatorField];

                          return (
                            <div key={gameType} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <button
                                type="button"
                                className={`teams-sport-card ${gameType} ${active ? 'active' : ''}`}
                                onClick={() =>
                                  setForm((prev) => {
                                    const next = prev.game_types || [];
                                    return {
                                      ...prev,
                                      game_types: active
                                        ? next.filter((item) => item !== gameType)
                                        : Array.from(new Set([...next, gameType])),
                                    };
                                  })
                                }
                                style={{
                                  borderColor: active ? currentThemeColor : 'rgba(255, 255, 255, 0.14)',
                                  backgroundColor: active ? `${currentThemeColor}1d` : '#F7F9FF',
                                  color: Colors.text,
                                }}
                                aria-pressed={active}
                              >
                                <div className="teams-sport-card-copy">
                                  <div className="teams-sport-card-title">{SPORT_LABELS[gameType].title}</div>
                                  <div className="teams-sport-card-subtitle">{SPORT_LABELS[gameType].subtitle}</div>
                                </div>
                                <div
                                  className={`teams-sport-card-check ${active ? 'active' : ''}`}
                                  style={{
                                    backgroundColor: active ? currentThemeColor : 'rgba(255, 255, 255, 0.16)',
                                    color: active ? currentTextColor : '#5F719A',
                                  }}
                                >
                                  {active ? <Check size={15} strokeWidth={3} /> : <span>+</span>}
                                </div>
                              </button>

                              {active && (
                                <div className="teams-sport-fields">
                                  <div className="form-group" style={{ margin: 0 }}>
                                    <label className="form-label">
                                      {SPORT_LABELS[gameType].title} Venue
                                      {form.status === 'active' && <span style={{ color: Colors.error }}> *</span>}
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <select
                                        className="form-input"
                                        style={{ flex: '1 1 0', minWidth: 0 }}
                                        value={isKnownVenue(venueValue, venueOptions) ? venueValue : ''}
                                        onChange={(e) => {
                                          if (e.target.value) setForm({ ...form, [venueField]: e.target.value });
                                        }}
                                      >
                                        <option value="">Pick a known venue…</option>
                                        {venueOptions.map((v) => (
                                          <option key={v.venue} value={v.venue}>
                                            {v.venue}
                                          </option>
                                        ))}
                                      </select>
                                      <input
                                        className="form-input"
                                        type="text"
                                        style={{ flex: '1 1 0', minWidth: 0 }}
                                        value={venueValue}
                                        onChange={(e) => setForm({ ...form, [venueField]: e.target.value })}
                                        placeholder="Or type a venue name"
                                      />
                                    </div>
                                  </div>

                                  <Input
                                    label={`${SPORT_LABELS[gameType].title} Commentator`}
                                    value={commentatorValue}
                                    onChange={(value) => setForm({ ...form, [commentatorField]: value })}
                                    placeholder="John Doe, Jane Smith"
                                    disabled={form.status === 'inactive'}
                                    required={form.status === 'active'}
                                    info="To display multiple commentators, list their names separated by commas."
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {activeEditorTab === 'logo' && (
                    <div className="teams-form-grid">
                      <div className="teams-section-header teams-full-width">
                        <div className="teams-section-title">Logo</div>
                        <div className="teams-section-subtitle">
                          Upload the main team logo and sport-specific logos for the selected sports.
                        </div>
                      </div>
                      <div className="teams-image-grid teams-full-width">
                        <LogoUpload
                          label="MainLogo"
                          helpText="Primary image used for the team card and list preview."
                          logoFile={mainLogoFile}
                          onLogoFileChange={handleMainLogoFileChange}
                          onClear={clearMainLogo}
                          previewUrl={mainPreview}
                          uploading={logoUploading}
                        />
                        {selectedGameTypes.includes('football') && (
                          <>
                            <LogoUpload
                              label="FootballRightLogo"
                              helpText="Right-facing football logo used for home team views."
                              logoFile={footballRightLogoFile}
                              onLogoFileChange={handleFootballRightLogoFileChange}
                              onClear={clearFootballRightLogo}
                              previewUrl={footballRightPreview}
                              uploading={logoUploading}
                            />
                            <LogoUpload
                              label="FootballLeftLogo"
                              helpText="Left-facing football logo used for away team views."
                              logoFile={footballLeftLogoFile}
                              onLogoFileChange={handleFootballLeftLogoFileChange}
                              onClear={clearFootballLeftLogo}
                              previewUrl={footballLeftPreview}
                              uploading={logoUploading}
                            />
                          </>
                        )}
                        {selectedGameTypes.includes('basketball') && (
                          <LogoUpload
                            label="BasketballLogo"
                            helpText="Basketball-specific logo shown when basketball is selected."
                            logoFile={basketballLogoFile}
                            onLogoFileChange={handleBasketballLogoFileChange}
                            onClear={clearBasketballLogo}
                            previewUrl={basketballPreview}
                            uploading={logoUploading}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="button-group teams-popup-actions" style={{ marginTop: '18px' }}>
                    <Button type="submit" className='btn-small' loading={saving || logoUploading}>
                      {editingTeamId ? 'Update School' : 'Save School'}
                    </Button>
                    <Button type="button" className='btn-small' variant="secondary" onClick={resetDraft} disabled={saving || logoUploading}>
                      Reset
                    </Button>
                    <Button type="button" className='btn-small' variant="outline" onClick={closeModal} disabled={saving || logoUploading}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
